import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Filter, Loader2, MapPin, ShieldCheck, User2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// Admin claims — campsite ownership handover requests. List page + inline
// approve/reject with an optional notes body. Backend endpoints:
//   GET   /admin/claims?status=
//   POST  /admin/claims/{id}/approve   { notes? }
//   POST  /admin/claims/{id}/reject    { notes? }
const STATUS_TABS = [
  { key: "pending",  label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all",      label: "All" },
];

export default function AdminClaims() {
  const { api } = useAuth();
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [notesFor, setNotesFor] = useState(null); // claim currently in reject-notes mode
  const [notesText, setNotesText] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/admin/claims", {
        params: { status: status === "all" ? undefined : status },
      });
      setItems(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load claims.");
    }
  }, [api, status]);

  useEffect(() => { load(); }, [load]);

  const act = async (c, action, notes) => {
    setBusyId(c.claim_id);
    try {
      await api.post(`/admin/claims/${c.claim_id}/${action}`, { notes: notes || null });
      toast.success(`Claim ${action}d.`);
      setNotesFor(null); setNotesText("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not update claim.";
      toast.error(detail);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="admin-claims-page">
      <div>
        <Link to="/admin" className="text-xs font-extrabold uppercase tracking-widest inline-flex items-center gap-1"
              style={{ color: "var(--brand)" }} data-testid="admin-claims-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Admin
        </Link>
        <h1 className="text-2xl md:text-3xl font-extrabold mt-2" style={{ color: "var(--on-surface)" }}>
          Campsite claims
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
          Requests from campers to take over an unclaimed / stale campsite listing.
        </p>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 no-scrollbar">
        <Filter className="w-4 h-4 opacity-60 shrink-0" />
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            data-testid={`claim-status-${t.key}`}
            className="whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-widest shrink-0 transition"
            style={{
              background: status === t.key ? "var(--brand)" : "color-mix(in srgb, var(--brand) 10%, transparent)",
              color: status === t.key ? "white" : "var(--brand)",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="card-elev p-4" style={{ color: "var(--premium)" }}>{error}</div>}
      {!items && (
        <div className="card-elev p-6 flex items-center gap-3" style={{ color: "var(--on-surface-muted)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading claims…
        </div>
      )}
      {items && items.length === 0 && (
        <div className="card-elev p-8 text-center" data-testid="admin-claims-empty">
          <ShieldCheck className="w-8 h-8 mx-auto opacity-40" style={{ color: "var(--on-surface-muted)" }} />
          <div className="mt-2 text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>No claims here</div>
          <div className="text-sm" style={{ color: "var(--on-surface-muted)" }}>Nothing waiting under this filter.</div>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4" data-testid="admin-claims-list">
          {items.map((c) => (
            <ClaimCard key={c.claim_id} c={c}
                       busy={busyId === c.claim_id}
                       notesOpen={notesFor === c.claim_id}
                       notesText={notesText}
                       setNotesText={setNotesText}
                       onApprove={() => act(c, "approve")}
                       onReject={() => act(c, "reject", notesText)}
                       onOpenReject={() => { setNotesFor(c.claim_id); setNotesText(""); }}
                       onCancelReject={() => { setNotesFor(null); setNotesText(""); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ClaimCard({ c, busy, notesOpen, notesText, setNotesText, onApprove, onReject, onOpenReject, onCancelReject }) {
  const isPending = (c.status || "pending") === "pending";
  const submitted = (c.submitted_at || "").slice(0, 10);
  return (
    <article className="card-elev p-4 flex flex-col gap-3"
             style={{ opacity: busy ? 0.6 : 1 }} data-testid={`claim-${c.claim_id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--brand)" }} />
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-base" style={{ color: "var(--on-surface)" }}>{c.campsite_name || c.campsite_id}</div>
          <div className="text-xs" style={{ color: "var(--on-surface-muted)" }}>
            Submitted {submitted}
            {c.claimer_name && (<> · by <b>{c.claimer_name}</b>{c.claimer_email && ` (${c.claimer_email})`}</>)}
          </div>
        </div>
        <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0"
              style={{
                background: c.status === "approved" ? "color-mix(in srgb, var(--brand) 12%, transparent)" :
                           c.status === "rejected" ? "color-mix(in srgb, #B45309 12%, transparent)" :
                                                     "color-mix(in srgb, var(--premium) 15%, transparent)",
                color: c.status === "approved" ? "var(--brand)" : c.status === "rejected" ? "#B45309" : "var(--premium)",
              }}>{c.status || "pending"}</span>
      </div>
      {c.evidence_url && (
        <a href={c.evidence_url} target="_blank" rel="noopener noreferrer"
           className="text-xs font-bold underline underline-offset-4" style={{ color: "var(--brand)" }}>
          View evidence
        </a>
      )}
      {c.reason && (
        <p className="text-sm" style={{ color: "var(--on-surface)" }}><b>Reason:</b> {c.reason}</p>
      )}
      {c.notes && (
        <p className="text-sm" style={{ color: "var(--on-surface-muted)" }}><b>Admin notes:</b> {c.notes}</p>
      )}
      {isPending && !notesOpen && (
        <div className="flex flex-wrap gap-2">
          <button onClick={onApprove} disabled={busy} className="btn btn-green" data-testid={`claim-approve-${c.claim_id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Approve</>}
          </button>
          <button onClick={onOpenReject} disabled={busy} className="btn btn-light" style={{ color: "#B45309" }}
                  data-testid={`claim-reject-open-${c.claim_id}`}>
            <X className="w-4 h-4" /> Reject with notes
          </button>
        </div>
      )}
      {isPending && notesOpen && (
        <div className="flex flex-col gap-2" data-testid={`claim-reject-form-${c.claim_id}`}>
          <textarea rows={3} maxLength={280} value={notesText} onChange={(e) => setNotesText(e.target.value)}
                    placeholder="Notes for the claimer (optional)"
                    className="w-full px-3 py-2.5 rounded-xl border text-sm resize-none"
                    style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }} />
          <div className="flex gap-2">
            <button onClick={onReject} disabled={busy} className="btn btn-light" style={{ color: "#B45309" }}
                    data-testid={`claim-reject-${c.claim_id}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reject"}
            </button>
            <button onClick={onCancelReject} className="btn btn-light">Cancel</button>
          </div>
        </div>
      )}
    </article>
  );
}
